import React from 'react'

/**
 * Canonical button for the dashboard. Encodes the shared design-system look so
 * every screen renders buttons identically instead of hand-rolling Tailwind.
 *
 *   <Button>Save</Button>                         primary (brand)
 *   <Button variant="secondary">Cancel</Button>   outlined
 *   <Button variant="ghost">Dismiss</Button>      text-only
 *   <Button variant="danger">Delete</Button>      destructive
 *   <Button size="sm" leftIcon={<Icon/>}>…        compact + icon
 *
 * Tokens only (brand-*, surface*, txt-*, border) so light/dark both work. One
 * disabled treatment (opacity-50) everywhere. Pass any native <button> prop.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md'

const BASE =
  'inline-flex items-center justify-center gap-1.5 font-semibold rounded-lg transition-colors outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap'

const SIZES: Record<ButtonSize, string> = {
  sm: 'text-xs px-2.5 py-1.5',
  md: 'text-sm px-4 py-2',
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700',
  secondary: 'bg-surface text-txt-primary border border-border hover:bg-surface-hover',
  ghost: 'text-txt-secondary hover:text-txt-primary hover:bg-surface-hover',
  danger: 'bg-red-600 text-white hover:bg-red-700',
}

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const Spinner = () => (
  <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
)

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { variant = 'primary', size = 'md', loading = false, leftIcon, rightIcon, className = '', children, disabled, ...rest },
    ref
  ) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={`${BASE} ${SIZES[size]} ${VARIANTS[variant]} ${className}`}
      {...rest}
    >
      {loading ? <Spinner /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  )
)
Button.displayName = 'Button'
